/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from 'react';

import type {
  ApolloCache,
  ApolloClient,
  ApolloQueryResult,
  DefaultContext,
  DocumentNode,
  ErrorLike,
  FetchResult,
  MutationUpdaterFunction,
  NetworkStatus,
  ObservableQuery,
  OperationVariables,
  SubscribeToMoreOptions,
  TypedDocumentNode,
  WatchQueryOptions,
} from '@apollo/client';
import {
  useLazyQuery as useBaseLazyQuery,
  useMutation as useBaseMutation,
  useQuery as useBaseQuery,
  useSubscription as useBaseSubscription,
  useSuspenseQuery as useBaseSuspenseQuery,
} from '@apollo/client/react';
import type {
  LazyQueryHookOptions as ApolloLazyQueryHookOptions,
  MutationHookOptions as ApolloMutationHookOptions,
  QueryHookOptions as ApolloQueryHookOptions,
  SubscriptionHookOptions as ApolloSubscriptionHookOptions,
  SuspenseQueryHookOptions as ApolloSuspenseQueryHookOptions,
  SkipToken,
} from '@apollo/client/react';

// Re-export skipToken for suspense queries
export { skipToken } from '@apollo/client/react';
export type { SkipToken } from '@apollo/client/react';

/**
 * MutationFunctionOptions type for Apollo Client 4 compatibility.
 * This replaces the removed MutationFunctionOptions from @apollo/client.
 */
export interface MutationFunctionOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
> {
  awaitRefetchQueries?: boolean;
  context?: DefaultContext;
  fetchPolicy?: 'network-only' | 'no-cache';
  /**
   * Called for every query the mutation would refetch or re-broadcast. Return
   * true for Apollo's default handling, false to leave the query alone.
   */
  onQueryUpdated?: (observableQuery: ObservableQuery<any>) => boolean;
  optimisticResponse?: TData | ((vars: TVariables) => TData);
  refetchQueries?: Array<string | { query: DocumentNode; variables?: any }>;
  update?: MutationUpdaterFunction<TData, TVariables, ApolloCache>;
  variables?: TVariables;
}

/**
 * Apollo Client 4 introduces DataState discriminated unions which change how `data` is typed.
 * This module provides backward-compatible types that match Apollo Client 3 behavior.
 * The `data` field is typed as `TData | undefined` instead of the new DataState union.
 */

/**
 * Backward-compatible QueryResult type for Apollo Client 4.
 * Removes the DataState discriminated union and provides a simple `data: TData | undefined` type.
 */
export interface QueryResult<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> {
  called: boolean;
  client: ApolloClient;
  data: TData | undefined;
  fetchMore: <
    TFetchData = TData,
    TFetchVars extends OperationVariables = TVariables,
  >(fetchMoreOptions: {
    updateQuery?: (
      previousQueryResult: TData,
      options: { fetchMoreResult: TFetchData; variables: TFetchVars }
    ) => TData;
    variables?: Partial<TFetchVars>;
  }) => Promise<ApolloQueryResult<TFetchData>>;
  loading: boolean;
  networkStatus: NetworkStatus;
  observable: ObservableQuery<TData, TVariables>;
  refetch: (variables?: Partial<TVariables>) => Promise<ApolloQueryResult<TData>>;
  reobserve: (
    newOptions?: Partial<WatchQueryOptions<TVariables, TData>>
  ) => Promise<ApolloQueryResult<TData>>;
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
  subscribeToMore: <
    TSubscriptionData = TData,
    TSubscriptionVariables extends OperationVariables = TVariables,
  >(
    options: SubscribeToMoreOptions<TData, TSubscriptionVariables, TSubscriptionData>
  ) => () => void;
  updateQuery: <TVars extends OperationVariables = TVariables>(
    mapFn: (previousQueryResult: TData, options: { variables?: TVars }) => TData
  ) => void;
  error?: ErrorLike;
  previousData?: TData;
  variables?: TVariables;
}

/**
 * Backward-compatible LazyQueryResult type for Apollo Client 4.
 */
export interface LazyQueryResult<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> extends QueryResult<TData, TVariables> {}

/**
 * Options for the lazy query execute function in Apollo Client 4.
 */
export interface LazyQueryExecOptions<TVariables extends OperationVariables = OperationVariables> {
  context?: DefaultContext;
  fetchPolicy?:
    | 'cache-first'
    | 'network-only'
    | 'no-cache'
    | 'standby'
    | 'cache-and-network'
    | 'cache-only';
  variables?: TVariables;
}

/**
 * Backward-compatible LazyQueryResultTuple type for Apollo Client 4.
 */
export type LazyQueryResultTuple<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> = [
  (options?: LazyQueryExecOptions<TVariables>) => Promise<ApolloClient.QueryResult<TData>>,
  LazyQueryResult<TData, TVariables>,
];

/**
 * Backward-compatible SuspenseQueryResult type for Apollo Client 4.
 */
export interface SuspenseQueryResult<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
> {
  client: ApolloClient;
  data: TData;
  fetchMore: <
    TFetchData = TData,
    TFetchVars extends OperationVariables = TVariables,
  >(fetchMoreOptions: {
    updateQuery?: (
      previousQueryResult: TData,
      options: { fetchMoreResult: TFetchData; variables: TFetchVars }
    ) => TData;
    variables?: Partial<TFetchVars>;
  }) => Promise<ApolloQueryResult<TFetchData>>;
  networkStatus: NetworkStatus;
  refetch: (variables?: Partial<TVariables>) => Promise<ApolloQueryResult<TData>>;
  subscribeToMore: <
    TSubscriptionData = TData,
    TSubscriptionVariables extends OperationVariables = TVariables,
  >(
    options: SubscribeToMoreOptions<TData, TSubscriptionVariables, TSubscriptionData>
  ) => () => void;
  error?: ErrorLike;
}

/**
 * This module redefines Apollo hooks for use by apollo-types.ts via codegen.  The point of redefining the hooks is to patch onError
 * handling for useMutation and also to provide a global default error handler that works with our existing Redux setup.
 */
export function useMutation<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: ApolloMutationHookOptions<TData, TVariables> = {}
) {
  const { onError, ...rest } = options;
  // Cast to any to work around Apollo 4's stricter types
  const res = useBaseMutation(mutation, rest as any);
  const [, { error }] = res;

  useEffect(() => {
    if (!error) return;
    if (onError) return onError(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return res;
}

/**
 * useQuery, useSubscription and useLazyQuery onError seem to function correctly so these hooks do not appear to need
 * a patch.  Consider adding default handleError to these hooks in the future if we want default error handling?
 */
export function useQuery<TData = any, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: ApolloQueryHookOptions<TData, TVariables>
): QueryResult<TData, TVariables> {
  // Apollo Client 4 returns DataState discriminated union; cast to our backward-compatible type
  return useBaseQuery<TData, TVariables>(query, options as any) as unknown as QueryResult<
    TData,
    TVariables
  >;
}

export function useLazyQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: ApolloLazyQueryHookOptions<TData, TVariables>
): LazyQueryResultTuple<TData, TVariables> {
  // Apollo Client 4 returns DataState discriminated union; cast to our backward-compatible type
  return useBaseLazyQuery<TData, TVariables>(
    query,
    options as any
  ) as unknown as LazyQueryResultTuple<TData, TVariables>;
}

export function useSubscription<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: ApolloSubscriptionHookOptions<TData, TVariables>
) {
  // Cast to any to work around Apollo 4's stricter types
  return useBaseSubscription<TData, TVariables>(query, options as any);
}

export function useSuspenseQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: SkipToken | ApolloSuspenseQueryHookOptions<TVariables>
): SuspenseQueryResult<TData, TVariables> {
  // Apollo Client 4 returns DataState discriminated union; cast to our backward-compatible type
  return useBaseSuspenseQuery<TData, TVariables>(
    query,
    options as any
  ) as unknown as SuspenseQueryResult<TData, TVariables>;
}

export type MutationHookOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
> = ApolloMutationHookOptions<TData, TVariables>;
export type LazyQueryHookOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
> = ApolloLazyQueryHookOptions<TData, TVariables>;
export type QueryHookOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
> = ApolloQueryHookOptions<TData, TVariables>;
export type SubscriptionHookOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
> = ApolloSubscriptionHookOptions<TData, TVariables>;
export type SuspenseQueryHookOptions<TVariables extends OperationVariables = OperationVariables> =
  ApolloSuspenseQueryHookOptions<TVariables>;

/**
 * Type namespace for hook options and results - used by generated codegen types.
 * Apollo Client 4 uses these namespaced types for better organization.
 */
export namespace useQuery {
  export type Options<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
  > = ApolloQueryHookOptions<TData, TVariables>;
  export type Result<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
  > = QueryResult<TData, TVariables>;
}

export namespace useLazyQuery {
  export type Options<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
  > = ApolloLazyQueryHookOptions<TData, TVariables>;
  export type Result<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
  > = LazyQueryResult<TData, TVariables>;
  export type ResultTuple<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
  > = LazyQueryResultTuple<TData, TVariables>;
}

export namespace useSuspenseQuery {
  export type Options<TVariables extends OperationVariables = OperationVariables> =
    ApolloSuspenseQueryHookOptions<TVariables>;
  export type Result<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
  > = SuspenseQueryResult<TData, TVariables>;
}

export namespace useMutation {
  export type Options<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
  > = ApolloMutationHookOptions<TData, TVariables>;
  export type Result<TData = any> = {
    called: boolean;
    client: ApolloClient;
    loading: boolean;
    reset: () => void;
    data?: TData | null;
    error?: ErrorLike;
  };
  export type ResultTuple<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
  > = [MutationFunction<TData, TVariables>, Result<TData>];
  /** The mutation function returned by useMutation hook */
  export type MutationFunction<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
  > = (options?: MutationFunctionOptions<TData, TVariables>) => Promise<FetchResult<TData>>;
}

export namespace useSubscription {
  export type Options<
    TData = any,
    TVariables extends OperationVariables = OperationVariables,
  > = ApolloSubscriptionHookOptions<TData, TVariables>;
  export type Result<TData = any> = {
    loading: boolean;
    data?: TData;
    error?: ErrorLike;
  };
}
